import { z } from 'zod'

/**
 * Les statistiques d'audience du site, telles que le back-office les lit.
 *
 * La source est PostHog, mais rien de PostHog ne traverse ce contrat : ni nom
 * d'événement, ni forme de réponse, ni vocabulaire d'analyse d'audience. Ce
 * que le back-office reçoit est déjà traduit dans les termes de la gérante —
 * c'est ce qui permettrait de changer d'outil de mesure sans toucher l'écran.
 */

/**
 * Les trois fenêtres d'observation, en jours. La liste est fermée : chaque
 * valeur coûte deux requêtes au service d'analyse (la période et celle qui la
 * précède), et un paramètre libre laisserait n'importe qui demander trois ans
 * d'historique à chaque rechargement de page.
 */
export const PERIOD_DAYS = [7, 30, 90] as const
export type PeriodDays = (typeof PERIOD_DAYS)[number]

export const periodDaysSchema = z.coerce
  .number()
  .int()
  .refine((n): n is PeriodDays => PERIOD_DAYS.includes(n as PeriodDays), {
    message: 'Période non reconnue',
  })

/**
 * Une valeur et son équivalent sur la période immédiatement précédente.
 *
 * `previous` est `null` quand cette période précédente n'a rien à comparer —
 * site trop récent, ou mesure activée depuis moins de deux périodes. Ce n'est
 * pas la même chose que zéro : zéro se compare (« -100 % »), l'absence ne se
 * compare pas. Confondre les deux ferait afficher « +0 % » là où la bonne
 * réponse est de ne rien afficher.
 */
export const comparedValueSchema = z.object({
  value: z.number().int().nonnegative(),
  previous: z.number().int().nonnegative().nullable(),
})
export type ComparedValue = z.infer<typeof comparedValueSchema>

/**
 * Une ligne de classement : un libellé déjà lisible et sa valeur.
 *
 * `label` est de la copie française produite par l'API, jamais un identifiant
 * technique — « Accueil — bandeau du haut », pas `accueil_hero`. La traduction
 * se fait à la lecture des événements, en un seul endroit.
 */
export const rankedItemSchema = z.object({
  label: z.string(),
  value: z.number().int().nonnegative(),
})
export type RankedItem = z.infer<typeof rankedItemSchema>

/**
 * L'état d'une rubrique, isolément des autres.
 *
 * Le service d'analyse répond rubrique par rubrique, et une requête peut
 * échouer pendant que les autres aboutissent — quota atteint sur l'une d'elles,
 * par exemple. L'écran doit alors montrer ce qu'il a plutôt que de tout perdre,
 * d'où cet état porté à l'échelle de la rubrique et non de la réponse entière.
 *
 * Il n'y a délibérément pas de troisième cas « vide » : une rubrique sans
 * données est un succès dont la valeur est zéro ou la liste est vide. C'est à
 * l'écran de dire « rien sur cette période », et il ne peut le dire que si
 * l'API distingue « je n'ai pas pu répondre » de « la réponse est zéro ».
 */
export function sectionSchema<T extends z.ZodTypeAny>(data: T) {
  return z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), data }),
    z.object({ status: z.literal('error') }),
  ])
}
export type Section<T> = { status: 'ok'; data: T } | { status: 'error' }

/** Visites et visiteurs, chacun comparé à la période précédente. */
export const trafficSchema = z.object({
  visits: comparedValueSchema,
  visitors: comparedValueSchema,
})
export type Traffic = z.infer<typeof trafficSchema>

/**
 * Les trois paliers du chemin vers la réservation, plus le départ vers le site
 * public de Planity — qui vit à côté et ne s'enchaîne pas aux autres.
 *
 * Aucun quatrième palier n'existe et aucun ne peut exister : la réservation
 * s'achève chez Planity, hors de portée de la mesure. Le contrat s'arrête donc
 * à « a ouvert le module », et le nommer ainsi est ce qui empêche l'écran de
 * laisser croire à une réservation confirmée.
 *
 * Les quatre valeurs sont comparées : l'écran en tire deux de ses quatre tuiles
 * de tête. La comparaison ne coûte rien de plus — la période précédente arrive
 * dans la même requête agrégée que la période courante.
 */
export const bookingPathSchema = z.object({
  visits: comparedValueSchema,
  bookingClicks: comparedValueSchema,
  widgetOpened: comparedValueSchema,
  planityExternal: comparedValueSchema,
})
export type BookingPath = z.infer<typeof bookingPathSchema>

/**
 * Téléphone, itinéraire, courriel : des intentions abouties hors du site.
 *
 * Comparés eux aussi, mais l'écran les lit en écart absolu (« +5 en hausse »)
 * plutôt qu'en pourcentage : sur des nombres à un ou deux chiffres, « +67 % »
 * dit trois appels de plus avec l'emphase d'une envolée.
 */
export const directContactsSchema = z.object({
  phone: comparedValueSchema,
  itinerary: comparedValueSchema,
  email: comparedValueSchema,
})
export type DirectContacts = z.infer<typeof directContactsSchema>

export const statisticsSchema = z.object({
  periodDays: periodDaysSchema,
  /**
   * Instant de la mesure, au format ISO. Les réponses sont mises en cache
   * quelques minutes pour tenir dans le quota du service d'analyse : sans cette
   * date, l'écran affirmerait une fraîcheur qu'il n'a pas.
   */
  measuredAt: z.string().datetime(),
  traffic: sectionSchema(trafficSchema),
  bookingPath: sectionSchema(bookingPathSchema),
  bookingClickOrigins: sectionSchema(z.array(rankedItemSchema)),
  topPages: sectionSchema(z.array(rankedItemSchema)),
  referrers: sectionSchema(z.array(rankedItemSchema)),
  devices: sectionSchema(z.array(rankedItemSchema)),
  serviceFamilies: sectionSchema(z.array(rankedItemSchema)),
  giftCard: sectionSchema(z.number().int().nonnegative()),
  directContacts: sectionSchema(directContactsSchema),
})
export type Statistics = z.infer<typeof statisticsSchema>
